import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useProfileForm } from '@/components/profile/hooks/useProfileForm';
import { ProfileFormFields } from '@/components/profile/components/ProfileFormFields';
import { ArrowRight, Loader2 } from 'lucide-react';

interface OnboardingProfileFormProps {
  onComplete: () => void;
  onSkip: () => void;
}

export const OnboardingProfileForm: React.FC<OnboardingProfileFormProps> = ({
  onComplete,
  onSkip,
}) => {
  const {
    formData,
    setFormData,
    isLoading,
    updateProfileMutation,
    addGoal,
    removeGoal,
  } = useProfileForm();

  const handleSubmit = async () => {
    updateProfileMutation.mutate(formData, {
      onSuccess: () => {
        onComplete();
      }
    });
  };

  const handleFormDataChange = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const isFormValid = formData.first_name.trim().length > 0;

  if (isLoading) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading your profile...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-center text-2xl">
          Let's Set Up Your Profile 🚀
        </CardTitle>
        <p className="text-center text-muted-foreground">
          Help us personalize your fitness journey by sharing some basic information
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <ProfileFormFields
          formData={formData}
          onFormDataChange={handleFormDataChange}
          onAddGoal={addGoal}
          onRemoveGoal={removeGoal}
        />

        <div className="flex gap-3 pt-4">
          <Button
            variant="ghost"
            onClick={onSkip}
            disabled={updateProfileMutation.isPending}
          >
            Skip for now
          </Button>
          
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || updateProfileMutation.isPending}
            className="flex-1"
          >
            {updateProfileMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Complete Setup
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};